import React, { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { BellRing, Download } from "lucide-react"
import { toast } from "sonner"
import api from "../../services/api"
import { ConfirmDialog } from "../ConfirmDialog"
import { Button } from "../ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Skeleton } from "../ui/skeleton"
import { Switch } from "../ui/switch"

const emptyConfiguration = {
  production: {
    latest: { version: "" },
    notice: { enabled: false, notifyBelow: { version: "" } },
  },
  testFlight: {
    latest: { version: "", build: 0 },
    notice: {
      enabled: false,
      notifyBelow: { version: "", build: 0 },
    },
  },
}

const clone = (value) => JSON.parse(JSON.stringify(value))

function compareVersions(left, right) {
  const parse = (value) =>
    value.split(".").map((part) => part.replace(/^0+(?=\d)/, ""))
  const leftParts = parse(left)
  const rightParts = parse(right)
  const count = Math.max(leftParts.length, rightParts.length)
  for (let index = 0; index < count; index += 1) {
    const leftValue = leftParts[index] ?? "0"
    const rightValue = rightParts[index] ?? "0"
    if (leftValue.length < rightValue.length) return -1
    if (leftValue.length > rightValue.length) return 1
    if (leftValue < rightValue) return -1
    if (leftValue > rightValue) return 1
  }
  return 0
}

export function validateReleaseDraft(channel, configuration) {
  const validVersion = (value) =>
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= 64 &&
    /^\d+(?:\.\d+)*$/.test(value)
  const latest = configuration?.latest
  const threshold = configuration?.notice?.notifyBelow
  if (!validVersion(latest?.version)) {
    return "Latest version must contain only dot-separated numbers."
  }
  if (!validVersion(threshold?.version)) {
    return "Notice threshold must contain only dot-separated numbers."
  }
  if (channel === "testFlight") {
    if (!Number.isSafeInteger(latest?.build) || latest.build < 0) {
      return "Latest build must be a nonnegative whole number."
    }
    if (!Number.isSafeInteger(threshold?.build) || threshold.build < 0) {
      return "Threshold build must be a nonnegative whole number."
    }
  }
  if (!configuration.notice.enabled) return null

  const versionComparison = compareVersions(
    threshold.version,
    latest.version
  )
  const thresholdIsNewer =
    versionComparison > 0 ||
    (channel === "testFlight" &&
      versionComparison === 0 &&
      threshold.build > latest.build)
  return thresholdIsNewer
    ? "Notice threshold cannot be newer than the latest release."
    : null
}

export function releaseThresholdPreview(channel, configuration) {
  const threshold = configuration?.notice?.notifyBelow
  if (!threshold?.version) return "Enter a threshold to preview the rule."
  const label =
    channel === "testFlight"
      ? `${threshold.version} (${threshold.build ?? 0})`
      : threshold.version
  return `Versions before ${label} will be notified; ${label} and newer will not.`
}

function ReleaseChannelCard({
  channel,
  title,
  configuration,
  onChange,
  onSave,
  onRemind,
  isSaving,
  isReminding,
  validationError,
  saveDisabled,
  reminderRevision,
  reminderUpdatedAt,
}) {
  const isTestFlight = channel === "testFlight"

  const update = (path, value) => {
    const next = clone(configuration)
    if (path === "latest.version") next.latest.version = value
    if (path === "latest.build") next.latest.build = Number(value)
    if (path === "notice.enabled") next.notice.enabled = value
    if (path === "notice.notifyBelow.version") {
      next.notice.notifyBelow.version = value
    }
    if (path === "notice.notifyBelow.build") {
      next.notice.notifyBelow.build = Number(value)
    }
    onChange(next)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          <Download className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>
          Configure availability separately from the optional notice campaign.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`${channel}-latest-version`}>
              Latest Available Version
            </Label>
            <Input
              id={`${channel}-latest-version`}
              value={configuration.latest.version}
              onChange={(event) =>
                update("latest.version", event.target.value.trim())
              }
              inputMode="numeric"
              placeholder="2026.8.2"
            />
          </div>
          {isTestFlight && (
            <div className="space-y-2">
              <Label htmlFor={`${channel}-latest-build`}>Latest Build</Label>
              <Input
                id={`${channel}-latest-build`}
                type="number"
                min="0"
                step="1"
                value={configuration.latest.build}
                onChange={(event) =>
                  update("latest.build", event.target.value)
                }
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <Label htmlFor={`${channel}-notice-enabled`}>
              Show Update Notice
            </Label>
            <p className="text-sm text-muted-foreground">
              Quietly publish latest-version changes while this is off.
            </p>
          </div>
          <Switch
            id={`${channel}-notice-enabled`}
            checked={configuration.notice.enabled}
            onCheckedChange={(checked) =>
              update("notice.enabled", checked)
            }
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`${channel}-threshold-version`}>
              Notify Versions Older Than
            </Label>
            <Input
              id={`${channel}-threshold-version`}
              value={configuration.notice.notifyBelow.version}
              onChange={(event) =>
                update(
                  "notice.notifyBelow.version",
                  event.target.value.trim()
                )
              }
              inputMode="numeric"
              placeholder="2026.8.0"
            />
          </div>
          {isTestFlight && (
            <div className="space-y-2">
              <Label htmlFor={`${channel}-threshold-build`}>
                Threshold Build
              </Label>
              <Input
                id={`${channel}-threshold-build`}
                type="number"
                min="0"
                step="1"
                value={configuration.notice.notifyBelow.build}
                onChange={(event) =>
                  update("notice.notifyBelow.build", event.target.value)
                }
              />
            </div>
          )}
        </div>

        <p className="rounded-md bg-muted p-3 text-sm">
          {releaseThresholdPreview(channel, configuration)}
        </p>
        {validationError && (
          <p className="text-sm text-destructive" role="alert">
            {validationError}
          </p>
        )}

        <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-muted-foreground">
            <div>Reminder revision: {reminderRevision}</div>
            <div>
              Last reminder:{" "}
              {reminderUpdatedAt
                ? new Date(reminderUpdatedAt).toLocaleString()
                : "Never"}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() => onRemind(channel)}
              disabled={isReminding}
            >
              <BellRing className="mr-2 h-4 w-4" />
              Remind Outdated Users Again
            </Button>
            <Button
              onClick={onSave}
              disabled={isSaving || saveDisabled}
            >
              {isSaving ? "Saving…" : "Save Channel Settings"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function AppReleaseConfigPanel() {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState(emptyConfiguration)
  const [remindChannel, setRemindChannel] = useState(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ["app-release-config"],
    queryFn: () => api.getAppReleaseConfig(),
  })

  useEffect(() => {
    if (!data) return
    setDraft({
      production: clone(data.production),
      testFlight: clone(data.testFlight),
    })
  }, [data])

  const saveMutation = useMutation({
    mutationFn: () => api.updateAppReleaseConfig(draft),
    onSuccess: (canonical) => {
      queryClient.setQueryData(["app-release-config"], canonical)
      toast.success("App release settings saved")
    },
    onError: (mutationError) => {
      toast.error(`Failed to save: ${mutationError.message}`)
    },
  })

  const remindMutation = useMutation({
    mutationFn: () => api.remindOutdatedUsersAgain(),
    onSuccess: (canonical) => {
      queryClient.setQueryData(["app-release-config"], canonical)
      setRemindChannel(null)
      toast.success("Eligible update campaigns re-armed")
    },
    onError: (mutationError) => {
      toast.error(`Failed to remind users: ${mutationError.message}`)
    },
  })

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>App Update Notices</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>App Update Notices</CardTitle>
          <CardDescription>
            The release configuration could not be loaded: {error.message}
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const productionError = validateReleaseDraft(
    "production",
    draft.production
  )
  const testFlightError = validateReleaseDraft(
    "testFlight",
    draft.testFlight
  )
  const saveDisabled = Boolean(productionError || testFlightError)

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">App Update Notices</h2>
        <p className="text-sm text-muted-foreground">
          Latest releases can be published quietly. Notices remain disabled
          until you explicitly enable each channel.
        </p>
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <ReleaseChannelCard
          channel="production"
          title="App Store"
          configuration={draft.production}
          onChange={(production) =>
            setDraft((current) => ({ ...current, production }))
          }
          onSave={() => saveMutation.mutate()}
          onRemind={setRemindChannel}
          isSaving={saveMutation.isPending}
          isReminding={remindMutation.isPending}
          validationError={productionError}
          saveDisabled={saveDisabled}
          reminderRevision={data?.reminderRevision ?? 0}
          reminderUpdatedAt={data?.reminderUpdatedAt}
        />
        <ReleaseChannelCard
          channel="testFlight"
          title="TestFlight"
          configuration={draft.testFlight}
          onChange={(testFlight) =>
            setDraft((current) => ({ ...current, testFlight }))
          }
          onSave={() => saveMutation.mutate()}
          onRemind={setRemindChannel}
          isSaving={saveMutation.isPending}
          isReminding={remindMutation.isPending}
          validationError={testFlightError}
          saveDisabled={saveDisabled}
          reminderRevision={data?.reminderRevision ?? 0}
          reminderUpdatedAt={data?.reminderUpdatedAt}
        />
      </div>

      <ConfirmDialog
        open={remindChannel !== null}
        onOpenChange={(open) => !open && setRemindChannel(null)}
        title="Remind outdated users again?"
        description="This increments the shared reminder revision. Eligible users on any enabled channel may see their current update campaign again. No push notification will be sent."
        confirmLabel="Remind Users Again"
        variant="default"
        onConfirm={() => remindMutation.mutate()}
      />
    </div>
  )
}

export default AppReleaseConfigPanel
